import { Box, Button } from "@material-ui/core";
import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  IFlightListData,
  FareclassOptionFilter,
  AlgomerchTag,
  mapAlgomerchTexts,
  FiatPrice,
  FlightShopStep,
  TripDetails,
  MODAL_ALERT,
  ModalScreens,
  ModalCategoryType,
  SELECTED_FLIGHT,
} from "redmond";
import { NoResults, useDeviceTypes } from "halifax";
import clsx from "clsx";
import { VIEWED_FLIGHT_LIST } from "redmond/build/apis/tysons";
import { IFlightListProps } from "./container";
import { ClientContext } from "../../../../App";
import {
  INITIAL_RESULT_SET_SIZE,
  SHOW_MORE_NUM,
} from "./components/FlightListInfo/textConstants";
import { FlightAlgomerchModal } from "../FlightAlgomerchModal";
import { ITripDetailsByTripId } from "../../reducer";
import {
  goToBook,
  goToReview,
} from "../../../hotel-shop/utils/queryStringHelpers";
import * as constants from "../../../book/actions/constants";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  CLEAR_FILTERS_CTA_TEXT,
  FLIGHTS_NOT_FOUND_SUBTITLE,
  FLIGHTS_NOT_FOUND_TITLE,
} from "./constants";
import { DesktopFlightList } from "./components/DesktopFlightList";
import { MobileFlightList } from "./components/MobileFlightList";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";

export enum ModalTypes {
  AlgomerchModal,
}

export const getSelectCtaCopy = (
  input: string,
  usePriceText?: boolean,
  isMobile?: boolean
) =>
  usePriceText ? (
    <>
      {!isMobile ? "Continue for " : ""}
      <strong>{input} per traveler</strong>
    </>
  ) : (
    `Select ${input}`
  );

export type IOpenModal = ModalTypes | false;

export const getSliceFareDetails = (args: {
  tripDetailsById: ITripDetailsByTripId;
  fareTrips: any;
}): TripDetails | null => {
  const { tripDetailsById, fareTrips } = args;
  if (!fareTrips.length) return null;
  let sliceDetails: TripDetails | null = null;
  sliceDetails = { ...tripDetailsById[fareTrips[0].trip] } as TripDetails;
  sliceDetails.fareDetails = [];
  fareTrips.forEach((fareTrip: any) => {
    const fareDetailByFareId = tripDetailsById[fareTrip.trip].fareDetails.find(
      (fareDetail) => fareDetail.id === fareTrip.fare
    );
    if (fareDetailByFareId && sliceDetails)
      sliceDetails.fareDetails.push(fareDetailByFareId);
  });
  sliceDetails.fareDetails.sort(
    (fare1, fare2) =>
      (fare1?.paxPricings?.[0].pricing.baseAmount.fiat.value || 0) -
      (fare2?.paxPricings?.[0].pricing.baseAmount.fiat.value || 0)
  );

  return sliceDetails as TripDetails;
};

export const FlightList = (props: IFlightListProps) => {
  const {
    flights,
    rewardsKey,
    packagesByOutboundFareSlice,
    packagesByReturnFareSlice,
    isFlightShopLoading,
    largestValueAccount,
    isTripDetailsLoading,
    tripDetailsById,
    fetchPackagesTripDetails,
    setPackagesChosenOutgoingSlice,
    setPackagesChosenReturnSlice,
    setPackagesFlightShopProgress,
    isOutgoing,
    isReturn,
    history,
    sortedFlights,
    maxPriceFilter,
    outgoingRecommendedFlight,
    returnRecommendedFlight,
    openMobileFlightDetailsModal,
    setOpenMobileFlightDetailsModal,
    hasFlightShopCallFailed,
    isFlightShopCallNotCalled,
    departureDate,
    returnDate,
    airports,
    hasAppliedNonFareclassFilter,
    resetFilters,
    viewedFlightListProperties,
    selectedOutgoingSliceProperties,
    selectedReturnSliceProperties,
    hotelShopDateRange,
    setHotelShopDateRange,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;

  const [faresToShow, setFaresToShow] = useState<any[]>([]);
  const [expandedFlight, setExpandedFlight] = useState("");
  const [selectedFareId, setClickedFareId] = useState("");
  const [fareTrips, setFareTrips] = useState([]);

  const [openModal, setOpenModal] = React.useState<IOpenModal>(false);
  const [selectedAlgomerchTag, setSelectedAlgomerchTag] =
    React.useState<AlgomerchTag>(AlgomerchTag.Cheapest);

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const handleClickAlgomerchTag = (tagText: string) => {
    const allTags = Object.keys(AlgomerchTag);
    const selectedTag = allTags.find((tag) =>
      tagText.includes(mapAlgomerchTexts[tag])
    );

    setSelectedAlgomerchTag(
      (selectedTag as AlgomerchTag) ?? AlgomerchTag.Cheapest
    );
  };

  const expandedFareDetails = useMemo(() => {
    const fetchedAllFareDetails = fareTrips.length
      ? fareTrips.reduce(
          (hasFetchedFareTrip: boolean, fareTrip: any) =>
            hasFetchedFareTrip && !!tripDetailsById[fareTrip.trip],
          true
        )
      : false;

    return fetchedAllFareDetails
      ? getSliceFareDetails({
          tripDetailsById,
          fareTrips,
        })
      : null;
  }, [fareTrips, tripDetailsById]);

  useEffect(() => {
    if (isFlightShopLoading) setFaresToShow([]);
  }, [isFlightShopLoading]);

  const setFetchMoreData = () => {
    const newPageSize = faresToShow.length + SHOW_MORE_NUM;
    return setTimeout(
      () => setFaresToShow(sortedFlights.slice(0, newPageSize)),
      500
    );
  };

  useEffect(() => {
    if (
      !isFlightShopLoading &&
      sortedFlights.length > 0 &&
      (isOutgoing || isReturn)
    ) {
      trackEvent({
        eventName: VIEWED_FLIGHT_LIST,
        properties: {
          ...viewedFlightListProperties,
        },
        encryptedProperties: [],
      });
    }
  }, [isFlightShopLoading, isOutgoing]);

  useEffect(() => {
    if (sortedFlights.length > 0) {
      if (!matchesMobile) {
        setFaresToShow(sortedFlights.slice(0, INITIAL_RESULT_SET_SIZE));
      } else {
        setFaresToShow(sortedFlights);
      }

      setExpandedFlight("");
    } else {
      setFaresToShow([]);
    }
    return clearTimeout(setFetchMoreData());
  }, [sortedFlights]);

  // TODO: this should be replaced with actual filters
  const fareClassFilter: FareclassOptionFilter = {
    basic: false,
    standard: false,
    enhanced: false,
    premium: false,
    luxury: false,
  };

  const handleFlightSelect = (fareTrips: any) => {
    const uniqueTripIds: string[] = Array.from(
      new Set(fareTrips.map((fareTrip: any) => fareTrip.trip))
    );
    uniqueTripIds.map((fareTrip: string) => {
      fetchPackagesTripDetails(history, fareTrip);
    });

    setFareTrips(fareTrips);
  };

  const handleSliceSelect = (sliceId: string, flight: IFlightListData) => {
    if (sliceId === expandedFlight) {
      setExpandedFlight("");
    } else {
      setExpandedFlight(sliceId);
      isOutgoing
        ? handleFlightSelect(flight.fares.map((fare: any) => fare.example))
        : handleFlightSelect(
            flight.fares.map((fare: any) => ({
              trip: fare.tripId,
              fare: fare.id,
            }))
          );
    }
  };

  const handleFareSelect = (
    flight: any,
    fareId: string,
    _idx: number,
    _limit?: FiatPrice | null
  ) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (flights && expandedFareDetails) {
      const tripId = expandedFareDetails?.id;

      if (isOutgoing) {
        const fareSliceId = flights.fares[fareId].outbound;
        const outgoingPackage = packagesByOutboundFareSlice[fareSliceId];

        setPackagesChosenOutgoingSlice(
          flight.slice,
          fareId,
          fareSliceId,
          tripId,
          flights.fareSlices[flights.fares[fareId].outbound].fareShelf.value,
          // whenever selecting a different departure flight, reset return ids
          true
        );
        if (outgoingPackage.lodgingStayDates) {
          setHotelShopDateRange(outgoingPackage.lodgingStayDates);
        }

        trackEvent({
          eventName: SELECTED_FLIGHT,
          properties: {
            ...selectedOutgoingSliceProperties,
            recommended_flight_selected:
              outgoingRecommendedFlight?.slice == flight.slice,
          },
          encryptedProperties: [],
        });
        setPackagesFlightShopProgress(FlightShopStep.ChooseReturn);
      } else {
        const fareSliceId = flights.fares[fareId].return;
        const returnPackage = packagesByReturnFareSlice[fareSliceId];

        setPackagesChosenReturnSlice(
          tripId,
          flight.slice,
          fareId,
          fareSliceId,
          flights.fareSlices[flights.fares[fareId].return].fareShelf.value
        );
        if (returnPackage.lodgingStayDates) {
          setHotelShopDateRange(returnPackage.lodgingStayDates);
        }

        if (matchesMobile || !isPackageCheckoutOptimizationExperiment) {
          setPackagesFlightShopProgress(FlightShopStep.ReviewItinerary);
          goToReview({ history });
        } else {
          goToBook({ history });
        }

        trackEvent({
          eventName: SELECTED_FLIGHT,
          properties: {
            ...selectedReturnSliceProperties,
            recommended_flight_selected:
              returnRecommendedFlight?.slice == flight.slice,
          },
          encryptedProperties: [],
        });
      }
    }
  };

  return (
    <Box className={clsx("packages-flight-list-root")}>
      {!isFlightShopLoading && !isFlightShopCallNotCalled && flights && (
        <>
          {(() => {
            switch (true) {
              case !!packagesByOutboundFareSlice &&
                faresToShow.length > 0 &&
                matchesMobile:
                return (
                  <MobileFlightList
                    flights={flights}
                    selectedFareId={selectedFareId}
                    expandedFlight={expandedFlight}
                    fareClassFilter={fareClassFilter}
                    maxPriceFilter={maxPriceFilter}
                    isOutgoing={isOutgoing}
                    packagesByOutboundFareSlice={packagesByOutboundFareSlice}
                    packagesByReturnFareSlice={packagesByReturnFareSlice}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    hotelShopDateRange={hotelShopDateRange}
                    isTripDetailsLoading={!!isTripDetailsLoading}
                    openMobileFlightDetailsModal={
                      !!openMobileFlightDetailsModal
                    }
                    airports={airports}
                    rewardsKey={rewardsKey}
                    isAgentPortal={isAgentPortal}
                    largestValueAccount={largestValueAccount}
                    expandedFareDetails={expandedFareDetails}
                    handleClickAlgomerchTag={handleClickAlgomerchTag}
                    setOpenModal={setOpenModal}
                    handleSliceSelect={handleSliceSelect}
                    setClickedFareId={setClickedFareId}
                    setExpandedFlight={setExpandedFlight}
                    handleFareSelect={handleFareSelect}
                    setOpenMobileFlightDetailsModal={
                      setOpenMobileFlightDetailsModal
                    }
                    faresToShow={faresToShow}
                    outgoingRecommendedFlight={outgoingRecommendedFlight}
                    returnRecommendedFlight={returnRecommendedFlight}
                  />
                );

              case !!packagesByOutboundFareSlice &&
                faresToShow.length > 0 &&
                !matchesMobile:
                return (
                  <DesktopFlightList
                    flights={flights}
                    selectedFareId={selectedFareId}
                    expandedFlight={expandedFlight}
                    fareClassFilter={fareClassFilter}
                    maxPriceFilter={maxPriceFilter}
                    isOutgoing={isOutgoing}
                    packagesByOutboundFareSlice={packagesByOutboundFareSlice}
                    packagesByReturnFareSlice={packagesByReturnFareSlice}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    hotelShopDateRange={hotelShopDateRange}
                    isTripDetailsLoading={!!isTripDetailsLoading}
                    openMobileFlightDetailsModal={
                      !!openMobileFlightDetailsModal
                    }
                    airports={airports}
                    rewardsKey={rewardsKey}
                    isAgentPortal={isAgentPortal}
                    largestValueAccount={largestValueAccount}
                    expandedFareDetails={expandedFareDetails}
                    handleClickAlgomerchTag={handleClickAlgomerchTag}
                    setOpenModal={setOpenModal}
                    handleSliceSelect={handleSliceSelect}
                    setClickedFareId={setClickedFareId}
                    setExpandedFlight={setExpandedFlight}
                    handleFareSelect={handleFareSelect}
                    setOpenMobileFlightDetailsModal={
                      setOpenMobileFlightDetailsModal
                    }
                    faresToShow={faresToShow}
                    sortedFlights={sortedFlights}
                    outgoingRecommendedFlight={outgoingRecommendedFlight}
                    returnRecommendedFlight={returnRecommendedFlight}
                    setFetchMoreData={setFetchMoreData}
                  />
                );
              case hasFlightShopCallFailed:
                trackEvent({
                  eventName: MODAL_ALERT,
                  properties: {
                    type: "packages_flight_list_error",
                    primary_button: constants.RELOAD,
                    screen: ModalScreens.FLIGHTS_SHOP,
                    category: ModalCategoryType.TROUBLE,
                    modal_subtitle: constants.ERROR_SUBTITLE,
                    modal_title: constants.ERROR_TITLE,
                    agent_title: constants.ERROR_TITLE,
                    agent_subtitle: constants.ERROR_TITLE,
                    funnel: "packages",
                    step: "flight_list",
                  },
                });

                return (
                  <Box className="no-results-container">
                    <NoResults
                      className="flight-list-no-results"
                      title={constants.ERROR_TITLE}
                      subtitle={constants.ERROR_SUBTITLE}
                    />
                    <button
                      onClick={() => window.location.reload()}
                      className="reload-button"
                    >
                      {constants.RELOAD}
                    </button>
                  </Box>
                );

              case hasAppliedNonFareclassFilter:
                return (
                  <Box className={clsx("no-results-container", "air-cx-v3")}>
                    <NoResults
                      className="flight-list-no-results"
                      title={FLIGHTS_NOT_FOUND_TITLE}
                      subtitle={FLIGHTS_NOT_FOUND_SUBTITLE}
                    />

                    <Box className="no-results-buttons-container">
                      <Button
                        onClick={resetFilters}
                        className={clsx("reset-filters-dates-button", "b2b")}
                      >
                        {CLEAR_FILTERS_CTA_TEXT}
                      </Button>
                    </Box>
                  </Box>
                );

              default:
                return null;
            }
          })()}
        </>
      )}

      <FlightAlgomerchModal
        selectedCategory={selectedAlgomerchTag}
        setSelectedCategory={setSelectedAlgomerchTag}
        openModal={openModal === ModalTypes.AlgomerchModal}
        onClose={() => setOpenModal(false)}
      />
    </Box>
  );
};
